import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiBook, FiPlus, FiEdit2, FiTrash2, FiUsers } from "react-icons/fi";
import homeworkService from "../../services/homeworkService";

const extractList = (res) => {
    const list = res?.data?.data ?? res?.data ?? [];
    return Array.isArray(list) ? list : [];
};

const getTeacherLabel = (teacher) => {
    if (!teacher) return "—";
    if (typeof teacher === "string") return teacher;
    return teacher.fullName || teacher.name || teacher.displayName || teacher.employeeCode || "—";
};

const formatDate = (date) => {
    if (!date) return "—";
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleDateString();
};

export default function HomeworkList() {
    const [homeworkList, setHomeworkList] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const loadHomework = async () => {
        setLoading(true);
        try {
            // NOTE: no teacher filter applied here — if your backend's
            // requireAdminOrTeacher middleware doesn't already scope results
            // to "my homework only" for teacher-role users, add
            // ?teacher=<myEmployeeId> here. Admins likely want to see everyone's.
            const res = await homeworkService.getHomeworkList();
            setHomeworkList(extractList(res));
        } catch (err) {
            setHomeworkList([]);
            toast.error(err.response?.data?.message || "Failed to load homework");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadHomework();
    }, []);

    const handleDelete = async (hw) => {
        if (
            !window.confirm(
                `Delete "${hw.title}"? Only possible if no students have submitted yet.`,
            )
        )
            return;
        try {
            await homeworkService.deleteHomework(hw._id);
            toast.success("Homework deleted");
            loadHomework();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to delete homework");
        }
    };

    const isOverdue = (dueDate) => new Date(dueDate) < new Date();

    if (loading) return <div className="page-loading">Loading homework...</div>;

    return (
        <div className="page-container">
            <div className="page-header">
                <h1>
                    <FiBook /> Homework
                </h1>
                <Link to="/homework/new" className="btn btn-primary">
                    <FiPlus /> Assign Homework
                </Link>
            </div>

            {homeworkList.length === 0 ? (
                <div className="empty-state">
                    <p>No homework assigned yet.</p>
                    <Link to="/homework/new" className="btn btn-primary">
                        <FiPlus /> Assign your first homework
                    </Link>
                </div>
            ) : (
                <div className="card">
                    <div className="table-wrapper">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Homework</th>
                                    <th>Subject</th>
                                    <th>Teacher</th>
                                    <th>Class</th>
                                    <th>Due Date</th>
                                    <th>Submissions</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {homeworkList.map((hw) => {
                                    const submittedCount = hw.submittedCount || 0;
                                    const totalStudents = hw.totalStudents || 0;
                                    const pct = totalStudents
                                        ? Math.round((submittedCount / totalStudents) * 100)
                                        : 0;
                                    const overdue = hw.isActive && isOverdue(hw.dueDate);

                                    return (
                                        <tr key={hw._id}>
                                            <td style={{ minWidth: 220 }}>
                                                <div style={{ fontWeight: 600 }}>{hw.title || "Untitled homework"}</div>
                                                {hw.description && (
                                                    <div style={{ color: "var(--text-muted)", fontSize: 12, maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {hw.description}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: 500 }}>{hw.subject?.name || "—"}</div>
                                                {hw.subject?.code && (
                                                    <span style={{ color: "var(--text-muted)", fontSize: 12 }}>{hw.subject.code}</span>
                                                )}
                                            </td>
                                            <td>{getTeacherLabel(hw.teacher)}</td>
                                            <td>
                                                <div>{hw.courseAdmitted || "—"}</div>
                                                <span style={{ color: "var(--text-muted)", fontSize: 12 }}>{hw.courseYear || "—"}</span>
                                            </td>
                                            <td>
                                                <div>{formatDate(hw.dueDate)}</div>
                                                {overdue && (
                                                    <span className="badge badge-full" style={{ marginTop: 4 }}>
                                                        Overdue
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ minWidth: 150 }}>
                                                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 5 }}>
                                                    <span>{submittedCount}/{totalStudents}</span>
                                                    <span style={{ color: "var(--text-muted)" }}>{pct}%</span>
                                                </div>
                                                <div style={{ height: 6, borderRadius: 999, background: "#e5e7eb", overflow: "hidden" }}>
                                                    <div
                                                        style={{
                                                            width: `${pct}%`,
                                                            height: "100%",
                                                            background: pct === 100 ? "var(--success)" : "var(--primary)",
                                                        }}
                                                    />
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`badge ${hw.isActive ? "badge-available" : "badge-muted"}`}>
                                                    {hw.isActive ? "Active" : "Inactive"}
                                                </span>
                                            </td>
                                            <td className="table-actions" style={{ justifyContent: "flex-end" }}>
                                                <button
                                                    className="btn btn-outline btn-sm"
                                                    onClick={() => navigate(`/homework/${hw._id}/submissions`)}
                                                >
                                                    <FiUsers /> Submissions
                                                </button>
                                                <button
                                                    className="btn btn-icon"
                                                    onClick={() => navigate(`/homework/${hw._id}/edit`)}
                                                    title="Edit"
                                                >
                                                    <FiEdit2 />
                                                </button>
                                                <button
                                                    className="btn btn-icon btn-danger"
                                                    onClick={() => handleDelete(hw)}
                                                    title="Delete"
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
